// @flow
import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useEffectOnce } from "hooks";
import { getDisplayName } from "data/constants";
import { type Location } from "history";
import queryString from "data/queryString";
import { parseSaveDocListFilterOperators } from "data/utils";
import { setDocsLayoutAction } from "data/actions";
import { produce } from "immer";
import isEqual from "lodash/isEqual";

type Props = {
    /** React-Router location */
    location: Location,
    /** current itemUri */
    itemUri: string,
};

/**
 * #46840 handler for setting default docList filter values
 */
const withDefaultFiltersHandler = (
    WrappedComponent: ComponentType<any>
): Node => {
    const HOC = (props: Props) => {
        const { location = window.location, itemUri } = props;
        const [filters, setFilters] = useState([]);
        const dispatch = useDispatch();
        const allow = useRef(true);
        const { filters: filtersFromStore = null } =
            useSelector((state) => state.docsLayout) || {};
        useEffectOnce(() => {
            // calculate filters from URL
            if (String(location?.search ?? "").length > 0) {
                setFilters(() => {
                    const qs = queryString.parse(location.search.substr(1));
                    const filters = [];
                    // let's see if any filter.* URL parameters exist
                    Object.keys(qs).forEach((key) => {
                        if (key.toLowerCase().startsWith("d.filter.")) {
                            const keySplit = key.split(".");
                            if (keySplit.length === 4) {
                                filters.push({
                                    name: keySplit[2],
                                    operator: parseSaveDocListFilterOperators(
                                        keySplit[3]
                                    ),
                                    values: [qs[key]],
                                });
                            }
                        }
                    });
                    return filters;
                });
            }
        });

        useEffect(() => {
            /* istanbul ignore if */
            if (!allow.current) return;
            /* istanbul ignore if */
            if (itemUri == null) return;
            if (!filtersFromStore?.length) return;
            // only allow one update
            allow.current = false;

            // merge filters from store with filters from URL
            const combinedFilters = produce(filtersFromStore, (draft) => {
                draft.forEach((filter, key) => {
                    const found =
                        filters?.find(
                            (f) =>
                                f.name.toUpperCase?.() ===
                                filter.name.toUpperCase?.()
                        ) || {};
                    draft[key] = { ...filter, ...found };
                });
            });
            // only dispatch if filters have changed
            if (!isEqual(filtersFromStore, combinedFilters))
                dispatch(setDocsLayoutAction({ filters: combinedFilters }));
        }, [dispatch, filters, filtersFromStore, itemUri]);
        return <WrappedComponent {...props} />;
    };
    HOC.displayName = `withDefaultFiltersHandler(${getDisplayName(
        WrappedComponent
    )})`;
    return HOC;
};

export default withDefaultFiltersHandler;
